import React, { useEffect, useState } from "react";

import {
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { MoreVert } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import {
  deleteGlobalOffersThunk,
  getGlobalOffersThunk,
  offerSelector,
  updateGlobalOffersThunk,
} from "store/reducers/global_offers/globalOffersSlice";
import StripedDataGrid from "components/StripedDataGrid";
import Loader from "components/Loader";
import moment from "moment";
import GlobalOfferMenu from "./offer_menu/GlobalOfferMenu";
import GlobalOffersMachineList from "./offer_menu/GlobalOffersMachineList";
import OfferImageDialog from "./offer_menu/OfferImageDialog";

const GlobalOfferList = () => {
  const theme = useTheme();
  const globalOffers = useSelector(offerSelector.selectAll);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 30,
    page: 0,
  });
  useEffect(() => {
    dispatch(
      getGlobalOffersThunk({
        ...paginationModel,
        page: paginationModel.page + 1,
      })
    );
  }, [paginationModel]);

  const [rowModesModel, setRowModesModel] = React.useState({});

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentItem, setCurrentItem] = React.useState(null);

  const handleMoreClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setCurrentItem(item);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (item) => () => {
    setDeleteItem(item.row);
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const [machineOpen, setMachineOpen] = useState(false);
  const closeMachineDialog = () => {
    setMachineOpen(false);
  };

  const [imageOpen, setImageOpen] = useState(false);
  const closeImageDialog = () => {
    setImageOpen(false);
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };

    delete newRow.associated_machine;
    delete newRow.offer_announcement_image;

    // console.log(newRow);
    dispatch(
      updateGlobalOffersThunk({
        id: newRow.id,
        data: {
          ...newRow,
          offer_from_date: moment(newRow.offer_from_date).format("YYYY-MM-DD"),
          offer_to_date: moment(newRow.offer_to_date).format("YYYY-MM-DD"),
        },
      })
    );
    const currentDate = moment();
    if (currentDate.isAfter(moment(newRow.offer_to_date), "day")) {
      return { ...updatedRow, expired: true };
    } else {
      return { ...updatedRow, expired: false };
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const globalOfferState = useSelector((state) => state.globalOffers.status);
  const totalItems = useSelector((state) => state.globalOffers.totalItems);

  const [errorSnack, setErrorSnack] = useState(false);
  const [successSnack, setSuccessSnack] = useState(false);

  const closeSnack = () => {
    setErrorSnack(false);
    setSuccessSnack(false);
  };

  useEffect(() => {
    if (
      globalOfferState?.success === "update" ||
      globalOfferState?.success === "delete"
    ) {
      setSuccessSnack(true);
      setErrorSnack(false);
    }
    if (
      globalOfferState?.error === "update" ||
      globalOfferState?.error === "delete"
    ) {
      setSuccessSnack(false);
      setErrorSnack(true);
    }
  }, [globalOfferState.success, globalOfferState.error]);

  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 1,
      maxWidth: 50,
      minWidth: 40,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "offer_name",
      headerName: "Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 180,
      minWidth: 160,
      headerClassName: "super-app-theme--header",
      editable: true,
      cellClassName: (params) => {
        // console.log("Expired", params.row.expired);
        if (params.row.expired) {
          return "expired-item";
        }

        return "";
      },
    },
    {
      field: "offer_code",
      headerName: "Offer code",
      flex: 1,
      maxWidth: 100,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      editable: true,
    },
    {
      field: "offer_announcement",
      headerName: "Offer announcement",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      editable: true,
    },
    {
      field: "offer_percentage",
      headerName: "Offer percentage",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 100,
      minWidth: 80,
      headerClassName: "super-app-theme--header",
      editable: true,
    },
    {
      field: "occasion",
      headerName: "Occasion",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 120,
      minWidth: 100,
      headerClassName: "super-app-theme--header",
      editable: true,
      type: "singleSelect",
      valueOptions: [
        { value: "fst", label: "Festival" },
        { value: "new", label: "New customer" },
        { value: "reg", label: "Registered user" },
      ],
    },
    {
      field: "offer_scope",
      headerName: "Offer scope",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 120,
      minWidth: 100,
      headerClassName: "super-app-theme--header",
      editable: true,
      type: "singleSelect",
      valueOptions: [
        { value: "all", label: "All machines" },
        { value: "sel", label: "Selected only" },
      ],
    },
    {
      field: "min_purchase_price",
      headerName: "Min purchase price",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 150,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      editable: true,
    },
    {
      field: "override_product_offers",
      headerName: "Override product offers",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 120,
      minWidth: 80,
      headerClassName: "super-app-theme--header",
      editable: true,
      type: "singleSelect",
      valueOptions: ["true", "false"],
    },
    // {
    //   field: "priority_level",
    //   headerName: "Priority level",
    //   flex: 1,
    //   headerAlign: "center",
    //   align: "center",
    //   maxWidth: 120,
    //   minWidth: 100,
    //   headerClassName: "super-app-theme--header",
    //   editable: true,
    // },

    {
      field: "offer_from_date",
      headerName: "From",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 150,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      editable: true,
      type: "date",
      valueGetter: (params) => {
        return moment(params.row.offer_from_date, "YYYY-MM-DD").toDate();
      },
    },
    {
      field: "offer_to_date",
      headerName: "To date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      maxWidth: 150,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      editable: true,
      type: "date",
      valueGetter: (params) => {
        return moment(params.row.offer_to_date, "YYYY-MM-DD").toDate();
      },
    },

    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      align: "right",
      cellClassName: "actions",
      headerClassName: "super-app-theme--header",
      getActions: (item) => {
        const isInEditMode = rowModesModel[item.id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(item.id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(item.id)}
              color="inherit"
            />,
          ];
        }

        const cellItems = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(item.id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(item)}
            color="inherit"
          />,
        ];

        cellItems.unshift(
          <GridActionsCellItem
            icon={<MoreVert />}
            label="More"
            className="textPrimary"
            onClick={(e) => handleMoreClick(e, item)}
            color="inherit"
          />
        );

        // if (item.row.offer_scope === "sel") {
        //   cellItems.unshift(
        //     <GridActionsCellItem
        //       icon={<MoreVert />}
        //       label="More"
        //       className="textPrimary"
        //       onClick={(e) => handleMoreClick(e, item)}
        //       color="inherit"
        //     />
        //   );
        // }

        return cellItems;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "& .super-app-theme--header": {
            backgroundColor: theme.palette.primary.lighter,
          },
          "& .expired-item": {
            color: "#faad14",
          },
        }}
      >
        <StripedDataGrid
          rows={globalOffers}
          columns={columns}
          autoHeight={true}
          editMode="row"
          loading={
            globalOfferState?.loading === "get" ||
            globalOfferState?.loading === "update" ||
            globalOfferState?.loading === "delete"
          }
          disableRowSelectionOnClick
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          sx={{ flex: 1 }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "odd" : "even"
          }
          pageSizeOptions={[30, 50, 100]}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalItems}
        />
      </Box>
      {(globalOfferState?.loading === "get" ||
        globalOfferState?.loading === "update" ||
        globalOfferState?.loading === "delete") && <Loader />}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={errorSnack || successSnack}
        autoHideDuration={6000}
        onClose={closeSnack}
      >
        <Alert
          onClose={closeSnack}
          severity={successSnack ? "success" : errorSnack ? "error" : "info"}
          sx={{ width: "100%" }}
        >
          {(() => {
            if (globalOfferState?.success === "update") {
              return "Offer item updated";
            } else if (globalOfferState?.success === "delete") {
              return "Offer item deleted";
            } else if (globalOfferState?.error === "update") {
              return "Error in updating offer";
            } else if (globalOfferState?.error === "delete") {
              return "Error in deleting offer";
            }
          })()}
        </Alert>
      </Snackbar>
      {deleteItem && (
        <Dialog open={deleteItem !== null} onClose={() => setDeleteItem(null)}>
          <DialogTitle id="alert-dialog-title">
            {`Do you want to delete ${deleteItem.offer_name} ?`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              This action can't be undone ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteItem(null)} autoFocus>
              Cancel
            </Button>
            <Button
              onClick={() => {
                console.log(deleteItem.id);
                dispatch(deleteGlobalOffersThunk(deleteItem));
                setDeleteItem(null);
              }}
              color="error"
            >
              Delete item
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {currentItem && (
        <GlobalOfferMenu
          offer={currentItem.row}
          handleClose={handleClose}
          anchorEl={anchorEl}
          setMachineOpen={setMachineOpen}
          setImageOpen={setImageOpen}
        />
      )}

      {machineOpen && (
        <GlobalOffersMachineList
          open={machineOpen}
          item={currentItem.row}
          handleClose={closeMachineDialog}
        />
      )}

      {imageOpen && (
        <OfferImageDialog
          open={imageOpen}
          item={currentItem.row}
          handleClose={closeImageDialog}
        />
      )}
    </>
  );
};

export default GlobalOfferList;
